import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, X, ImageIcon, Edit3, Eye, EyeOff } from "lucide-react";
import { api, formatApiError } from "@/lib/apiClient";

const BLANK = { slot: "", image_url: "", alt: "", caption: "", is_visible: true };

export default function AdminAssets() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);

  const load = () => api.get("/admin/assets").then((r) => setItems(r.data || []));
  useEffect(() => { load(); }, []);

  const startNew = () => { setEditing({ ...BLANK }); setOpen(true); };
  const startEdit = (a) => { setEditing({ ...a }); setOpen(true); };

  const save = async () => {
    if (!editing.slot?.trim()) return toast.error("Slot is required");
    if (!editing.image_url?.trim()) return toast.error("Image URL is required");
    try {
      if (editing.id) await api.put(`/admin/assets/${editing.id}`, editing);
      else await api.post("/admin/assets", editing);
      toast.success("Saved"); setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this image? The slot will fall back to its built-in default.")) return;
    await api.delete(`/admin/assets/${id}`); toast.success("Deleted"); load();
  };

  const toggle = async (a) => {
    await api.put(`/admin/assets/${a.id}`, { ...a, is_visible: !a.is_visible });
    load();
  };

  return (
    <div data-testid="admin-assets" className="space-y-8">
      <header className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[11px] uppercase tracking-[0.22em] text-brand-charcoal/55">Centralised Image Manager</div>
          <h1 className="editorial-h1 text-5xl mt-2">{items.length} images.</h1>
          <p className="text-sm text-brand-charcoal/65 mt-2 max-w-xl">Every image on Pop Panda lives here. Replace, hide or delete any slot — the website updates instantly.</p>
        </div>
        <button onClick={startNew} data-testid="aa-new" className="btn-pill btn-dark text-sm"><Plus size={16} /> Add / replace image</button>
      </header>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="aa-grid">
        {items.map((a) => (
          <div key={a.id} data-testid={`aa-card-${a.id}`} className="card-elevated overflow-hidden">
            <div className="aspect-[4/3] overflow-hidden bg-brand-muted relative">
              {a.image_url
                ? <img src={a.image_url} alt={a.alt || a.slot} className={`h-full w-full object-cover ${a.is_visible ? "" : "grayscale opacity-60"}`} />
                : <div className="h-full w-full grid place-items-center"><ImageIcon size={36} className="text-brand-charcoal/30" /></div>}
              {!a.is_visible && <span className="absolute top-3 right-3 text-[10px] uppercase tracking-[0.18em] border border-brand-charcoal/30 rounded-full px-2.5 py-1 bg-white">Hidden</span>}
            </div>
            <div className="p-5">
              <div className="text-[10px] uppercase tracking-[0.22em] text-brand-red font-mono">{a.slot}</div>
              {a.alt && <div className="text-sm mt-2 line-clamp-2">{a.alt}</div>}
              {a.caption && <div className="text-xs text-brand-charcoal/55 mt-1 italic line-clamp-1">"{a.caption}"</div>}
            </div>
            <div className="p-3 flex items-center justify-between gap-2 border-t border-brand-line">
              <button onClick={() => toggle(a)} className="btn-pill btn-ghost text-xs border border-brand-line">{a.is_visible ? (<><EyeOff size={12} /> Hide</>) : (<><Eye size={12} /> Show</>)}</button>
              <div className="flex gap-2">
                <button data-testid={`aa-edit-${a.id}`} onClick={() => startEdit(a)} className="btn-pill btn-ghost text-xs border border-brand-line"><Edit3 size={12} /> Edit</button>
                <button onClick={() => del(a.id)} className="btn-pill btn-ghost text-xs text-brand-red"><Trash2 size={12} /></button>
              </div>
            </div>
          </div>
        ))}
        {items.length === 0 && <div className="text-brand-charcoal/60 col-span-full py-12 text-center">No images yet. Add the first one.</div>}
      </div>

      {open && editing && (
        <div className="fixed inset-0 z-50 bg-brand-charcoal/40 grid place-items-center p-4">
          <div className="bg-white rounded-3xl w-full max-w-2xl max-h-[90vh] overflow-y-auto p-8">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="text-[11px] uppercase tracking-[0.22em] text-brand-charcoal/55">Image slot</div>
                <div className="font-display text-3xl mt-1">{editing.id ? "Edit image" : "Add / replace"}</div>
              </div>
              <button onClick={() => setOpen(false)}><X /></button>
            </div>

            {editing.image_url && (
              <div className="aspect-[16/9] overflow-hidden rounded-2xl bg-brand-muted mb-5">
                <img src={editing.image_url} alt="Preview" className="h-full w-full object-cover" />
              </div>
            )}

            <div className="space-y-4">
              <div>
                <label className="text-[11px] uppercase tracking-[0.22em] text-brand-charcoal/55">Slot key</label>
                <input data-testid="aa-f-slot" className="pp-input font-mono" placeholder="e.g. home.students, about.gallery1" value={editing.slot} onChange={(e) => setEditing({ ...editing, slot: e.target.value })} disabled={!!editing.id} />
                <p className="text-[10px] text-brand-charcoal/55 mt-1">Slots are the addresses each page reads from. Common slots: <code className="text-brand-red">home.students</code>, <code className="text-brand-red">home.inventor.popsicle</code>, <code className="text-brand-red">about.gallery1</code>, <code className="text-brand-red">about.gallery2</code>, <code className="text-brand-red">about.gallery3</code>, <code className="text-brand-red">about.innovation</code>, <code className="text-brand-red">popandago.hero</code>.</p>
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-[0.22em] text-brand-charcoal/55">Image URL</label>
                <input data-testid="aa-f-url" className="pp-input" placeholder="https://..." value={editing.image_url} onChange={(e) => setEditing({ ...editing, image_url: e.target.value })} />
                <p className="text-[10px] text-brand-charcoal/55 mt-1">Paste a hosted URL (Unsplash, Cloudinary, S3, etc.). Direct file upload coming soon.</p>
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-[0.22em] text-brand-charcoal/55">Alt text (for accessibility &amp; SEO)</label>
                <input data-testid="aa-f-alt" className="pp-input" value={editing.alt || ""} onChange={(e) => setEditing({ ...editing, alt: e.target.value })} />
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-[0.22em] text-brand-charcoal/55">Caption (optional)</label>
                <input data-testid="aa-f-cap" className="pp-input" value={editing.caption || ""} onChange={(e) => setEditing({ ...editing, caption: e.target.value })} />
              </div>
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!editing.is_visible} onChange={(e) => setEditing({ ...editing, is_visible: e.target.checked })} /> Visible on website</label>
            </div>

            <div className="mt-8 flex justify-end gap-3">
              <button onClick={() => setOpen(false)} className="btn-pill btn-ghost text-sm">Cancel</button>
              <button onClick={save} data-testid="aa-f-save" className="btn-pill btn-dark text-sm">Save image</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
